import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Lock,
    Eye,
    Bug,
    Network,
    ServerCog,
    ArrowRight,
    CheckCircle2,
    AlertTriangle,
    Star
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernCybersecurity = () => {
    const services = [
        {
            icon: <Shield size={28} />,
            title: 'Enterprise Firewall Installation',
            description: 'Advanced perimeter defense with next-generation firewall technology.',
            features: ['Deep packet inspection', 'Intrusion prevention', 'Application control', 'Threat intelligence']
        },
        {
            icon: <Lock size={28} />,
            title: 'Network Encryption Solutions',
            description: 'End-to-end encryption for all your communication channels.',
            features: ['AES-256 encryption', 'VPN deployment', 'Secure tunneling', 'Key management']
        },
        {
            icon: <ServerCog size={28} />,
            title: 'Access Control Systems',
            description: 'Multi-factor authentication and identity management.',
            features: ['Zero-trust architecture', 'Role-based access', 'Biometric integration', 'SSO solutions']
        },
        {
            icon: <Eye size={28} />,
            title: 'Security Monitoring Platforms',
            description: '24/7 threat detection and incident response.',
            features: ['SIEM integration', 'Real-time alerts', 'Automated response', 'SOC services']
        },
        {
            icon: <Bug size={28} />,
            title: 'Vulnerability Assessments',
            description: 'Comprehensive security audits and penetration testing.',
            features: ['Automated scanning', 'Manual testing', 'Compliance reporting', 'Remediation']
        },
        {
            icon: <Network size={28} />,
            title: 'Network Security',
            description: 'Complete network infrastructure protection.',
            features: ['Traffic analysis', 'DDoS protection', 'Secure networking', 'Segmentation']
        }
    ];

    const stats = [
        { number: '99.9%', label: 'Uptime Guarantee' },
        { number: '24/7', label: 'Security Monitoring' },
        { number: '500+', label: 'Enterprises Protected' },
        { number: '<2min', label: 'Threat Response Time' }
    ];

    const features = [
        { icon: <Shield size={24} />, title: 'Protected', text: 'Enterprise security' },
        { icon: <Lock size={24} />, title: 'Encrypted', text: 'Data protection' },
        { icon: <Eye size={24} />, title: 'Monitored', text: '24/7 surveillance' },
        { icon: <AlertTriangle size={24} />, title: 'Responsive', text: 'Rapid response' }
    ];

    const testimonials = [
        {
            name: 'Sarah Johnson',
            role: 'CISO, TechCorp',
            content: 'Their layered defense approach has completely transformed our security posture. We now have visibility and protection we never had before.',
            rating: 5
        },
        {
            name: 'Michael Chen',
            role: 'IT Director, GlobalTech',
            content: 'Best-in-class monitoring and rapid incident response. The team is proactive and always available when we need them. Highly recommended.',
            rating: 5
        }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Cybersecurity"
                subtitle="Protecting your data, systems, and operations"
                breadcrumbs={[
                    { label: 'Solutions', path: '/solutions' },
                    { label: 'Cybersecurity' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                Enterprise Security
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Robust Security Frameworks with
                                <span className="highlight"> Layered Defense</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We implement robust security frameworks with layered defense against threats
                                and secured communications. Our comprehensive approach protects your
                                organization from evolving cyber threats while ensuring business continuity.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Get Protected Now
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    Security Assessment
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="jk-solutions-stats">
                <div className="jk-solutions-stats__container">
                    <div className="jk-solutions-stats__grid">
                        {stats.map((stat, index) => (
                            <div key={index} className="jk-solutions-stats__item">
                                <div className="jk-solutions-stats__number">{stat.number}</div>
                                <div className="jk-solutions-stats__label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Lock size={16} />
                            Security Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Our Security
                            <span className="highlight"> Services</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Comprehensive cybersecurity services designed to protect your digital assets
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-solutions-services__card-title">{service.title}</h3>
                                <p className="jk-solutions-services__card-description">{service.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="jk-solutions-testimonials">
                <div className="jk-solutions-testimonials__container">
                    <div className="jk-solutions-testimonials__header">
                        <h2 className="jk-solutions-testimonials__title">
                            What Our Clients Say
                        </h2>
                    </div>

                    <div className="jk-solutions-testimonials__grid">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="jk-solutions-testimonials__card">
                                <div className="jk-solutions-testimonials__stars">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} size={18} />
                                    ))}
                                </div>
                                <p className="jk-solutions-testimonials__quote">
                                    "{testimonial.content}"
                                </p>
                                <div className="jk-solutions-testimonials__author">
                                    <div className="jk-solutions-testimonials__avatar">
                                        {testimonial.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="jk-solutions-testimonials__name">{testimonial.name}</div>
                                        <div className="jk-solutions-testimonials__role">{testimonial.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernCybersecurity;
